import config from 'lib/config';
import { NotFoundError, ServerError } from 'lib/CustomErrors';
import createDealerListing, { DealerListing } from 'lib/models/DealerListing';
import { createDealer, Dealer, NearbyDealer } from 'lib/models/Dealer';
import { DealerPage, createDealerPage } from 'lib/models/DealerPage';
import { DealerReview, createDealerReview } from 'lib/models/DealerReview';
import {
  DealerPageApiFetchResponse,
  DealerPageApiListingsDealerListing,
  DealerPageApiResponseDealer,
  DealerPageApiResponseDealerPage,
  DealerPageApiResponseReview,
} from 'lib/apis/DealerPageApi/types';
import createDealerHours, { DealerHours } from 'lib/models/DealerHours';

function convertDealerReview(review: DealerPageApiResponseReview): DealerReview {
  return createDealerReview({
    id: review.id,
    title: review.review_title,
    author: review.reviewer_name,
    content: review.review,
    recommended: review.recommended,
    overallRating: review.overall_rating,
    processRating: review.process_rating,
    serviceRating: review.service_rating,
    priceRating: review.price_rating,
    helpRating: review.help_rating,
    writtenAt: review.written_at,
  });
}

function convertNearbyDealer(dealer: DealerPageApiResponseDealer): NearbyDealer {
  return {
    name: dealer.dealer_name,
    dealerPageUrl: dealer.dealer_page_url,
  };
}

function convertDealerHours(dealerHours: Record<string, string>): Record<string, string> {
  return {
    Mon: dealerHours.mon,
    Tue: dealerHours.tues,
    Wed: dealerHours.wed,
    Thu: dealerHours.thu,
    Fri: dealerHours.fri,
    Sat: dealerHours.sat,
    Sun: dealerHours.sun,
  };
}

function convertDealer(dealer: DealerPageApiResponseDealer, reviews: DealerReview[]): Dealer {
  return createDealer(
    {
      displayName: dealer.display_name,
      remoteId: dealer.remote_id,
      providerName: dealer.provider_name,
      dealerName: dealer.dealer_name,
      street: dealer.street,
      city: dealer.city,
      state: dealer.state,
      zip: dealer.zip,
      phoneRegional: dealer.phone_regional,
      website: dealer.website,
      dealerHours: convertDealerHours(dealer.dealer_hours),
      nearbyDealers: (dealer.nearby_dealers || []).map(convertNearbyDealer),
    },
    reviews
  );
}

function convertDealerPage(dealerPage: DealerPageApiResponseDealerPage): DealerPage {
  const reviews = dealerPage.reviews.map(convertDealerReview);
  const dealer = convertDealer(dealerPage.dealer, reviews);

  return createDealerPage({
    url: dealerPage.url,
    titleTag: dealerPage.title_tag,
    description: dealerPage.description,
    metadescription: dealerPage.metadescription,
    mapUrl: dealerPage.map_url,
    email: dealerPage.email,
    phone: dealerPage.phone,
    dealer,
    reviews,
    enabled: dealerPage.enabled,
  });
}

const NEARBY_DEALERS_LIMIT = 30;

export async function fetchDealerPage(dealerPageUrl: string): Promise<DealerPage> {
  const url = `${config.dealerPagesApiUrl}/${dealerPageUrl}?nearby_limit=${NEARBY_DEALERS_LIMIT}`;

  const response = await fetch(url);
  const { dealer_page: dealerPage } = (await response.json()) as DealerPageApiFetchResponse;

  switch (response.status) {
    case 404:
      throw new NotFoundError('Dealer page not found');
    case 500:
      throw new ServerError('Server error');
    default:
      return convertDealerPage(dealerPage);
  }
}

export async function fetchDealerPageClient(dealerPageUrl: string): Promise<DealerPage> {
  const response = await fetch(`/api/dealer-pages/${dealerPageUrl}`);

  if (!response.ok) {
    switch (response.status) {
      case 404:
        throw new NotFoundError('Dealer page not found');
      case 500:
        throw new ServerError('Server error');
      default:
        throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  return response.json();
}

export async function fetchDealerHours(dealerPageUrl: string): Promise<DealerHours> {
  const url = `${config.dealerPagesApiUrl}/${dealerPageUrl}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Error fetching dealer hours');
  }

  const { dealer_page: dealerPage } = (await response.json()) as DealerPageApiFetchResponse;
  return createDealerHours({ hoursData: dealerPage.dealer.dealer_hours });
}

export type DealerListingsResponse = {
  records: DealerListing[];
  total: number;
};

export function convertDealerListing(
  dealerListing: DealerPageApiListingsDealerListing
): DealerListing {
  return createDealerListing({
    vin: dealerListing.vin,
    year: dealerListing.year,
    make: dealerListing.make,
    model: dealerListing.model,
    mileageUnformatted: dealerListing.mileage_unformatted,
    priceUnformatted: dealerListing.price_unformatted,
    vdpUrl: dealerListing.vdp_url,
    photoUrls: dealerListing.photo_urls,
  });
}

export async function fetchDealerListings(
  remoteDealerId: string,
  providerName: string
): Promise<DealerListingsResponse> {
  const url = `${config.coreApiUrl}/search?remote_dealer_id=${remoteDealerId}&provider_name=${providerName}`;

  const response = await fetch(url);
  const { records, total_count: total } = await response.json();

  if (!response.ok) {
    throw new Error('Error fetching listings');
  }

  return { records: records.map(convertDealerListing), total };
}

export default {
  fetchDealerPage,
  fetchDealerListings,
};
